import React, { useState } from 'react';
import { 
  HelpCircle, 
  Inbox, 
  ArrowRight, 
  Star, 
  GripVertical,
  Plus,
  Tag,
  Users,
  CheckCircle,
  Zap,
  X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';

const HELP_SECTIONS = [
  {
    icon: Plus,
    title: 'Snabb Capture',
    description: 'Skriv en uppgift i fältet högst upp och tryck Enter. Den hamnar direkt i Inbox.',
    tip: 'Dumpa allt ur huvudet - sortera sen!'
  },
  {
    icon: GripVertical,
    title: 'Kanban-flödet',
    description: 'Dra uppgifter mellan kolumnerna för att visa progress.',
    columns: [
      { name: 'Inbox', desc: 'Nya, osorterade' },
      { name: 'Next Up', desc: 'Ska göras snart' },
      { name: 'In Progress', desc: 'Jobbar på nu' },
      { name: 'Done', desc: 'Klart!' }
    ]
  },
  {
    icon: Star,
    title: 'Stjärnmarkera',
    description: 'Klicka på stjärnan för att markera viktiga uppgifter. Hitta dem snabbt under "Stjärnmärkta" i menyn.',
    tip: 'Max 3-5 stjärnor = fokus!'
  },
  {
    icon: Zap,
    title: 'Dagens Fokus',
    description: 'Välj dina top 3 viktigaste uppgifter för dagen. Öppna en task och slå på "Dagens fokus".',
    tip: 'Börja dagen med att välja 3 fokusuppgifter'
  },
  {
    icon: Tag,
    title: 'Taggar & Filter',
    description: 'Skapa egna taggar (projekt, kategori) via "Hantera taggar". Filtrera sen efter tagg, prioritet eller tid.',
  },
  {
    icon: Users,
    title: 'Samarbete',
    description: 'Bjud in andra till ditt workspace via kugghjulet bredvid workspace-namnet. De får en email-inbjudan.',
  },
];

const KEYBOARD_SHORTCUTS = [
  { keys: ['Enter'], action: 'Skapa task (i capture-fältet)' },
  { keys: ['Klick'], action: 'Öppna task för redigering' },
  { keys: ['⭐'], action: 'Stjärnmarkera/avmarkera' },
];

export function HelpGuide() {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <button
          className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-zinc-400 hover:bg-white/5 hover:text-zinc-100 transition-colors"
          data-testid="help-guide-btn"
        >
          <HelpCircle className="w-4 h-4" strokeWidth={1.5} />
          <span className="text-sm">Hjälp & Guide</span>
        </button>
      </DialogTrigger>
      <DialogContent className="bg-[#141416] border-white/10 text-zinc-50 max-w-lg max-h-[85vh] overflow-hidden flex flex-col" data-testid="help-guide-modal">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="text-xl font-semibold font-['Outfit'] flex items-center gap-2">
            <Zap className="w-5 h-5 text-yellow-400" />
            Så funkar FlowFocus
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-6 pt-4 pr-2">
          {/* Intro */}
          <div className="p-4 rounded-xl bg-gradient-to-br from-zinc-800/50 to-zinc-900/50 border border-white/5">
            <p className="text-sm text-zinc-300 leading-relaxed">
              FlowFocus hjälper dig <span className="text-zinc-100 font-medium">fånga idéer snabbt</span>, 
              {' '}<span className="text-zinc-100 font-medium">organisera</span> dem visuellt och 
              {' '}<span className="text-zinc-100 font-medium">fokusera</span> på det viktigaste.
            </p>
          </div>

          {/* Main sections */}
          <div className="space-y-4">
            {HELP_SECTIONS.map((section, idx) => (
              <HelpSection key={idx} {...section} />
            ))}
          </div>

          {/* Quick flow */}
          <div className="p-4 rounded-xl bg-[#09090B] border border-white/5">
            <h3 className="text-sm font-medium text-zinc-100 mb-3">Typiskt flöde</h3>
            <div className="flex items-center gap-2 text-xs text-zinc-400 flex-wrap">
              <span className="px-2 py-1 rounded bg-zinc-800">Idé dyker upp</span>
              <ArrowRight className="w-3 h-3" />
              <span className="px-2 py-1 rounded bg-zinc-800">Quick capture</span>
              <ArrowRight className="w-3 h-3" />
              <span className="px-2 py-1 rounded bg-zinc-800">Inbox</span>
              <ArrowRight className="w-3 h-3" />
              <span className="px-2 py-1 rounded bg-zinc-800">Prioritera</span>
              <ArrowRight className="w-3 h-3" />
              <span className="px-2 py-1 rounded bg-emerald-800">Done!</span>
            </div>
          </div>

          {/* Pro tips */}
          <div className="p-4 rounded-xl bg-yellow-500/5 border border-yellow-500/10">
            <h3 className="text-sm font-medium text-yellow-400 mb-2 flex items-center gap-2">
              <Zap className="w-4 h-4" />
              Pro-tips
            </h3>
            <ul className="text-xs text-zinc-400 space-y-1.5">
              <li>• <span className="text-zinc-300">Töm Inbox dagligen</span> - flytta till rätt kolumn</li>
              <li>• <span className="text-zinc-300">Max 3 fokusuppgifter</span> - mindre är mer</li>
              <li>• <span className="text-zinc-300">Stjärnmarkera sparsamt</span> - allt kan inte vara viktigt</li>
              <li>• <span className="text-zinc-300">Stora tasks?</span> - Bryt ner i mindre steg</li>
            </ul>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function HelpSection({ icon: Icon, title, description, tip, columns }) {
  return (
    <div className="space-y-2">
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-lg bg-zinc-800 flex items-center justify-center flex-shrink-0">
          <Icon className="w-4 h-4 text-zinc-300" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-medium text-zinc-100">{title}</h3>
          <p className="text-xs text-zinc-500 mt-0.5">{description}</p>
          
          {columns && (
            <div className="flex gap-2 mt-2 flex-wrap">
              {columns.map((col, idx) => (
                <div key={idx} className="flex items-center gap-1.5">
                  <div className={`w-1.5 h-1.5 rounded-full ${
                    idx === 0 ? 'bg-zinc-500' :
                    idx === 1 ? 'bg-blue-500' :
                    idx === 2 ? 'bg-yellow-500' :
                    'bg-emerald-500'
                  }`} />
                  <span className="text-xs text-zinc-400">{col.name}</span>
                  {idx < columns.length - 1 && (
                    <ArrowRight className="w-3 h-3 text-zinc-600 ml-1" />
                  )}
                </div>
              ))}
            </div>
          )}
          
          {tip && (
            <p className="text-xs text-emerald-400/80 mt-1.5 italic">💡 {tip}</p>
          )}
        </div>
      </div>
    </div>
  );
}
